// pages/privacy-policy.js  (Next.js Pages router)
// or app/privacy-policy/page.js (App Router — keep the default export)

import React from "react";
import "/Users/nathalia/fenixmedia/src/components/Privacy-policy.css";
import Footer from "../Footer";

/**
 * ---------- CONFIG ----------
 * Update these values to match your business details.
 */
const POLICY = {
  companyName: "Fenix Media",
  siteLabel: "the “Site” or “we”",
  domain: "www.fenixmtl.com",          // your public site URL
  contactEmail: "info@fenix360mtl.com",   // your preferred privacy/contact email
  postalAddress: "Add your mailing address here", // e.g., "123 Example St, Montreal, QC, H2X 1Y4, Canada"
  lastUpdated: "2025-09-17",           // YYYY-MM-DD
  usesShopify: false,                  // set true if your store runs on Shopify
  usesGoogleAnalytics: true,           // set true if you use GA
  gaHelpLink: "https://policies.google.com/technologies/partner-sites",
  gaOptOutLink: "https://tools.google.com/dlpage/gaoptout",
  daaOptOutLink: "https://optout.aboutads.info/",
  naiEducationLink:
    "http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work",
  facebookAdPrefsLink: "https://www.facebook.com/adpreferences/ad_settings/",
  googleAdSettingsLink: "https://www.google.com/settings/ads/anonymous",
  dpAuthorityLink:
    "https://plainte.cai.gouv.qc.ca/",
};


/**
 * ---------- SECTION HELPERS ----------
 */

const ShopifyDisclosure = () =>
  POLICY.usesShopify ? (
    <p>
      <strong>Disclosure for a business purpose:</strong> shared with our
      processor Shopify.
    </p>
  ) : null;

const GoogleAnalyticsBlock = () =>
  POLICY.usesGoogleAnalytics ? (
    <>
      <li className="small">
        We use Google Analytics to help us understand how visitors use the
        Site. Learn how Google uses data{" "}
        <a href={POLICY.gaHelpLink} target="_blank" rel="noreferrer">
          here
        </a>
        . You can opt out of Google Analytics{" "}
        <a href={POLICY.gaOptOutLink} target="_blank" rel="noreferrer">
          here
        </a>
        .
      </li>
    </>
  ) : null;

/**
 * ---------- PAGE ----------
 */

export default function PrivacyPolicyPage() {
  return (
    <main className="policy-wrap">
      <h1>Privacy Policy</h1>
      <p className="muted">
        Last updated: {new Date(POLICY.lastUpdated).toLocaleDateString()}
      </p>

      <section className="section">
        <p>
          This Privacy Policy describes how {POLICY.domain} ({POLICY.siteLabel})
          collects, uses, and discloses your Personal Information when you visit
          or make a purchase from the Site.
        </p>
      </section>

      <section className="section">
        <h2>Collecting Personal Information</h2>
        <p>
          When you visit the Site, we collect certain information about your
          device, your interaction with the Site, and information necessary to
          process your purchases. We may also collect additional information if
          you contact us for customer support. In this Privacy Policy, we refer
          to any information that can uniquely identify an individual
          (including the information below) as “Personal Information”. See the
          list below for more information about what Personal Information we
          collect and why.
        </p>

        <h3>Device information</h3>
        <ul>
          <li className="small">
            <strong>Examples of Personal Information collected:</strong>{" "}
            version of web browser, IP address, time zone, cookie information,
            what pages or products you view, search terms, and how you interact
            with the Site.
          </li>
          <li className="small">
            <strong>Purpose of collection:</strong> to load the Site accurately
            for you, and to perform analytics on Site usage to optimize our
            Site.
          </li>
          <li className="small">
            <strong>Source of collection:</strong> collected automatically when
            you access our Site using cookies, log files, web beacons, tags, or
            pixels.
          </li>
        </ul>

        <ShopifyDisclosure />

        <h3>Order information</h3>
        <ul>
          <li className="small">
            <strong>Examples of Personal Information collected:</strong> name,
            billing address, shipping address, payment information (e.g., last
            four digits of card, payment token), email address, and phone
            number.
          </li>
          <li className="small">
            <strong>Purpose of collection:</strong> to provide products or
            services to you to fulfill our contract, to process your payment
            information, arrange for shipping, and provide you with invoices
            and/or order confirmations; to communicate with you; to screen
            orders for potential risk or fraud; and—when in line with the
            preferences you have shared with us—to provide you with information
            or advertising relating to our products or services.
          </li>
          <li className="small">
            <strong>Source of collection:</strong> collected from you.
          </li>
        </ul>

        <ShopifyDisclosure />
      </section>

      <section className="section">
        <h2>Minors</h2>
        <p>
          The Site is not intended for individuals under the age of 18. We do
          not intentionally collect Personal Information from children. If you
          are the parent or guardian and believe your child has provided us with
          Personal Information, please contact us at{" "}
          <a href={`mailto:${POLICY.contactEmail}`}>{POLICY.contactEmail}</a> to
          request deletion.
        </p>
      </section>

      <section className="section">
        <h2>Sharing Personal Information</h2>
        <p>
          We share your Personal Information with service providers to help us
          provide our services and fulfill our contracts with you, as described
          above.
        </p>
        <p>
          We may also share your Personal Information to comply with applicable
          laws and regulations, to respond to a subpoena, search warrant or
          other lawful request for information we receive, or to otherwise
          protect our rights.
        </p>
      </section>

      <section className="section">
        <h2>Behavioural Advertising</h2>
        <p>
          As described above, we use your Personal Information to provide you
          with targeted advertisements or marketing communications we believe
          may be of interest to you. For example:
        </p>
        <ul>
          <GoogleAnalyticsBlock />
          <li className="small">
            We share information about your use of the Site, your purchases, and
            your interaction with our ads on other websites with our advertising
            partners. We collect and share some of this information directly
            with our advertising partners, and in some cases through the use of
            cookies or other similar technologies (which you may consent to,
            depending on your location).
          </li>
        </ul>

        <p>
          For more information about how targeted advertising works, visit the
          Network Advertising Initiative’s (NAI) educational page:{" "}
          <a href={POLICY.naiEducationLink} target="_blank" rel="noreferrer">
            {POLICY.naiEducationLink}
          </a>
          .
        </p>

        <p>You can opt out of targeted advertising by:</p>
        <ul>
          <li className="small">
            FACEBOOK:{" "}
            <a
              href={POLICY.facebookAdPrefsLink}
              target="_blank"
              rel="noreferrer"
            >
              Facebook Ad Preferences
            </a>
          </li>
          <li className="small">
            GOOGLE:{" "}
            <a
              href={POLICY.googleAdSettingsLink}
              target="_blank"
              rel="noreferrer"
            >
              Google Ad Settings
            </a>
          </li>
        </ul>

        <p>
          Additionally, you can opt out of some of these services by visiting
          the Digital Advertising Alliance’s opt-out portal:{" "}
          <a href={POLICY.daaOptOutLink} target="_blank" rel="noreferrer">
            {POLICY.daaOptOutLink}
          </a>
          .
        </p>
      </section>

      <section className="section">
        <h2>Using Personal Information</h2>
        <p>
          We use your Personal Information to provide our services to you, which
          includes: offering products or services, processing payments, and (if
          applicable) shipping and fulfillment of orders, as well as keeping you
          up to date on new products, services, and offers.
        </p>
      </section>

      <section className="section">
        <h2>Lawful Basis (GDPR)</h2>
        <p>
          If you are a resident of the European Economic Area (EEA), we process
          your personal information under the following lawful bases:
        </p>
        <ul>
          <li>your consent;</li>
          <li>the performance of the contract between you and the Site;</li>
          <li>compliance with our legal obligations; and</li>
          <li>
            our legitimate interests, which do not override your fundamental
            rights and freedoms.
          </li>
        </ul>
      </section>

      <section className="section">
        <h2>Automatic Decision-Making</h2>
        <p>
          If you are a resident of the EEA, you have the right to object to
          processing based solely on automated decision-making (which includes
          profiling), when that decision-making has a legal effect on you or
          otherwise significantly affects you.
        </p>
        <p>
          We do not engage in fully automated decision-making that has a legal
          or otherwise significant effect using customer data.
        </p>
      </section>

      <section className="section">
        <h2>Your Rights (GDPR)</h2>
        <p>
          If you are a resident of the EEA, you have the right to access the
          Personal Information we hold about you, to port it to a new service,
          and to ask that your Personal Information be corrected, updated, or
          erased. If you would like to exercise these rights, please contact us
          at{" "}
          <a href={`mailto:${POLICY.contactEmail}`}>{POLICY.contactEmail}</a>.
        </p>
        <p>
          Your Personal Information may be initially processed in the EEA and
          then transferred outside of Europe for storage and further processing,
          including to Canada and the United States.
        </p>
      </section>

      <section className="section">
        <h2>Cookies</h2>
        <p>
          A cookie is a small amount of information that’s downloaded to your
          computer or device when you visit our Site. We use a number of
          different cookies, including functional, performance, advertising, and
          social media or content cookies. Cookies make your browsing experience
          better by allowing the website to remember your actions and
          preferences (such as login and region selection).
        </p>
        <p>
          The length of time that a cookie remains on your device depends on
          whether it is a “persistent” or “session” cookie. Session cookies last
          until you stop browsing and persistent cookies last until they expire
          or are deleted. Most of the cookies we use are persistent and will
          expire between 30 minutes and two years from the date they are
          downloaded to your device.
        </p>
        <p>
          You can control and manage cookies in various ways. Please keep in
          mind that removing or blocking cookies can negatively impact your user
          experience and parts of our website may no longer be fully
          accessible.
        </p>
        <p>
          Most browsers automatically accept cookies, but you can choose whether
          or not to accept cookies through your browser controls, often found in
          your browser’s <span className="kbd">Tools</span> or{" "}
          <span className="kbd">Preferences</span> menu. For more information on
          how to modify your browser settings or how to block, manage, or filter
          cookies, see resources such as{" "}
          <a href="https://www.allaboutcookies.org" target="_blank" rel="noreferrer">
            www.allaboutcookies.org
          </a>
          .
        </p>
        <p>
          Additionally, please note that blocking cookies may not completely
          prevent how we share information with third parties such as our
          advertising partners. To exercise your rights or opt out of certain
          uses of your information by these parties, please follow the
          instructions in the “Behavioural Advertising” section above.
        </p>
      </section>

      <section className="section">
        <h2>Do Not Track</h2>
        <p>
          Because there is no consistent industry understanding of how to
          respond to “Do Not Track” signals, we do not alter our data collection
          and usage practices when we detect such a signal from your browser.
        </p>
      </section>

      <section className="section">
        <h2>Changes</h2>
        <p>
          We may update this Privacy Policy from time to time in order to
          reflect, for example, changes to our practices or for other
          operational, legal, or regulatory reasons.
        </p>
      </section>

      <section className="section">
        <h2>Contact</h2>
        <p>
          For more information about our privacy practices, if you have
          questions, or if you would like to make a complaint, please contact us
          by e-mail at{" "}
          <a href={`mailto:${POLICY.contactEmail}`}>{POLICY.contactEmail}</a>{" "}
          or by mail using the details below:
        </p>
        <p>{POLICY.postalAddress}</p>
        <p className="small">
          If you are not satisfied with our response to your complaint, you have
          the right to lodge your complaint with the relevant data protection
          authority. If applicable, you can find more information here:{" "}
          <a href={POLICY.dpAuthorityLink} target="_blank" rel="noreferrer">
            {POLICY.dpAuthorityLink}
          </a>
          .
        </p>
      </section>
      <Footer></Footer>
    </main>
  );
}

/**
 * Optional (Next.js App Router): export metadata
 * export const metadata = {
 *   title: "Privacy Policy | Fenix Media",
 *   description: "How Fenix Media collects, uses, and discloses personal information.",
 *   robots: "index,follow",
 * };
 */
